/**
 * Shared formatting utilities for the COGSLOG frontend.
 * @module utils/format
 */

/**
 * Format a number as US currency string.
 * Handles negative values, large numbers, and optional compact notation.
 *
 * @param {number} value - The numeric value to format
 * @param {Object} [options] - Formatting options
 * @param {boolean} [options.compact=false] - Use compact notation ($1.2K) for chart axes
 * @param {number} [options.decimals=2] - Number of decimal places
 * @returns {string} Formatted currency string (e.g., "$1,234.56")
 * @example
 * formatCurrency(1234.5)           // "$1,234.50"
 * formatCurrency(1234.5, { compact: true })  // "$1.2K"
 * formatCurrency(-50.1)            // "-$50.10"
 */
export function formatCurrency(value, options = {}) {
  const { compact = false, decimals = 2 } = options;
  if (value == null || isNaN(value)) return '$0.00';

  if (compact) {
    const abs = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    if (abs >= 1000000) return `${sign}$${(abs / 1000000).toFixed(1)}M`;
    if (abs >= 1000) return `${sign}$${(abs / 1000).toFixed(1)}K`;
    return `${sign}$${abs.toFixed(0)}`;
  }

  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  return `${sign}$${abs.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  })}`;
}

/**
 * Format a number as a percentage string.
 *
 * @param {number} value - The percentage value (e.g., 5.2 means 5.2%)
 * @param {number} [decimals=1] - Number of decimal places
 * @returns {string} Formatted percentage string (e.g., "5.2%")
 * @example
 * formatPercent(5.234)   // "5.2%"
 * formatPercent(-3.1)    // "-3.1%"
 */
export function formatPercent(value, decimals = 1) {
  if (value == null || isNaN(value)) return '0.0%';
  return `${value.toFixed(decimals)}%`;
}

/**
 * Format a number as compact US currency (e.g., $1.2K for thousands).
 * Useful for chart labels and space-constrained UI elements.
 *
 * @param {number} amount - The numeric value to format
 * @returns {string} Compact currency string (e.g., "$1.2K", "$45.00")
 * @example
 * formatCompactCurrency(1500)   // "$1.5K"
 * formatCompactCurrency(250)    // "$250.00"
 * formatCompactCurrency(0)      // "$0.00"
 */
export function formatCompactCurrency(amount) {
  if (amount == null || isNaN(amount)) return '$0.00';
  const abs = Math.abs(amount);
  const sign = amount < 0 ? '-' : '';
  if (abs >= 1000) return `${sign}$${(abs / 1000).toFixed(1)}K`;
  return `${sign}$${abs.toFixed(2)}`;
}

/**
 * Format a normalized price value with its canonical unit.
 *
 * Phase 11 Wave 6b: backend (Wave 6a) computes
 * `normalized_price = unit_price / base_units_per_pack` (with NULLIF guard) and
 * exposes it on 5 read paths (sku-catalog, line-items, price-history,
 * vendor-comparison, and the cross-org Data Browser). When either curated value
 * is missing the backend returns NULL — this helper renders that as the em-dash
 * fallback so un-curated rows display consistently across the 4 frontend tables.
 *
 * Resolution chain for the unit suffix:
 *   1. row.canonical_unit (preferred — backend-computed COALESCE of override + sku default)
 *   2. row.base_unit (fallback — raw sku_catalog rows that don't carry canonical_unit)
 *   3. literal 'unit' (defensive default — should not appear in practice)
 *
 * @param {number|null|undefined} value - Normalized $/unit price (e.g. 0.69)
 * @param {Object} [row] - The row object containing canonical_unit or base_unit
 * @returns {string} Formatted display string (e.g. "$0.69/egg") or "—" when null
 * @example
 * formatNormalized(0.6944, { canonical_unit: 'egg' })   // "$0.69/egg"
 * formatNormalized(null, { canonical_unit: 'egg' })     // "—"
 * formatNormalized(1.25, { base_unit: 'ml' })           // "$1.25/ml"
 * formatNormalized(0)                                   // "$0.00/unit"
 */
export function formatNormalized(value, row) {
  if (value == null) return '—';
  const unit = (row && (row.canonical_unit || row.base_unit)) || 'unit';
  return `$${Number(value).toFixed(2)}/${unit}`;
}
